import React from 'react';
import { Box, TableRow, TableCell, Typography, Tooltip } from '@mui/material';
import NotesIcon from '@mui/icons-material/Notes';

/**
 * NoteEventRow - Renders a note event
 *
 * Displays a note with its description and the user who created it
 *
 * Optimized with React.memo to prevent unnecessary re-renders
 */
const NoteEventRow = React.memo(function NoteEventRow({
  event,
  isChild = false
}) {
  const username = event.cognitoUsername || event.cognitoEmail || event.userId;
  const timestamp = new Date(event.timestamp || event.createdAt);

  // Background color based on whether it's a child event
  const backgroundColor = isChild ? '#fffdf5' : '#fff8e1';
  const borderColor = isChild ? '4px solid #ffe082' : '4px solid #fbc02d';

  return (
    <TableRow
      hover
      sx={{
        backgroundColor,
        borderLeft: borderColor,
        '& .MuiTableCell-root': { borderBottom: '1px solid rgba(224, 224, 224, 0.4)' },
        '&:hover': {
          backgroundColor,
          filter: 'brightness(0.98)'
        }
      }}
    >
      {/* Event Type Icon */}
      <TableCell align="center" sx={{ width: 40 }}>
        <Tooltip title="Note">
          <NotesIcon sx={{ color: 'warning.main', fontSize: 28 }} />
        </Tooltip>
      </TableCell>

      {/* Expand/Collapse Column (empty for notes) */}
      <TableCell align="center" sx={{ width: 40 }} />

      {/* Event Details */}
      <TableCell>
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {event.description}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            by {username}
          </Typography>
        </Box>
      </TableCell>

      {/* Timestamp */}
      <TableCell>
        <Typography variant="caption">
          {timestamp.toLocaleString()}
        </Typography>
      </TableCell>

      {/* Location (notes typically don't have location) */}
      <TableCell>
        <Typography variant="caption" color="text.secondary">
          —
        </Typography>
      </TableCell>
    </TableRow>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if these changed
  return (
    prevProps.event.eventId === nextProps.event.eventId &&
    prevProps.event.description === nextProps.event.description &&
    prevProps.isChild === nextProps.isChild
  );
});

export default NoteEventRow;
